import { Link, useLocation } from "react-router-dom";
import { useAttendee } from "../context/AttendeeContext";
import { useEvent } from "../context/EventContext";
import { useHeader } from "../context/HeaderContext";
import { useSections } from "../context/SectionsContext";
import { useBanners } from "../context/BannersContext";
import { BannerCarousel } from "./BannerCarousel";
import PoweredByWave from "./PoweredByWave";
import { useThemeColors } from "../hooks/useThemeColors";
import { useOverlayRegistration } from "../hooks/useAndroidBackButton";
import {
  Calendar,
  Users,
  MapPin,
  Settings,
  X,
  Menu,
  MessageSquare,
  Bell,
  Sparkles,
  Bookmark,
  Star,
  Info,
} from "lucide-react";
import { ICON_OPTIONS } from "../lib/constants";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Get icon component from ICON_OPTIONS by name
const getSectionIcon = (iconName) => {
  const iconOption = ICON_OPTIONS.find((option) => option.name === iconName);
  return iconOption
    ? iconOption.icon
    : ICON_OPTIONS.find((option) => option.name === "list").icon;
};

export function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const { attendee, getAttendeeInitials, getAttendeeName } = useAttendee();
  const {
    event,
    loading: eventLoading,
    getEventLogoUrl,
    getEventName,
    getEventDateRange,
  } = useEvent();
  const { sections } = useSections();
  const { banners } = useBanners();
  const { themeColor } = useThemeColors();

  // Register sidebar with overlay manager for Android back button handling
  useOverlayRegistration(
    "sidebar",
    sidebarOpen,
    () => setSidebarOpen(false),
    1
  ); // Lower priority than modals

  // Debug logging

  const { actions, searchProps } = useHeader();

  // Get the current event slug from the path
  const pathParts = location.pathname.split("/").filter(Boolean);
  const eventSlug = pathParts[1]; // /e/:eventSlug format

  // Use paths with /e/ prefix
  const basePath = eventSlug ? `/e/${eventSlug}` : "";

  const navigation = [
    {
      name: "Messages",
      href: `${basePath}/messages`,
      icon: MessageSquare,
      current: location.pathname === `${basePath}/messages`,
    },
    {
      name: "Notifications",
      href: `${basePath}/notifications`,
      icon: Bell,
      current: location.pathname === `${basePath}/notifications`,
    },
  ];

  const socialNavigation = [
    {
      name: "Copilot",
      href: `${basePath}/copilot`,
      icon: Sparkles,
      current: location.pathname === `${basePath}/copilot`,
    },
    {
      name: "Attendees",
      href: `${basePath}/attendees`,
      icon: Users,
      current: location.pathname === `${basePath}/attendees`,
    },
    {
      name: "Favorites",
      href: `${basePath}/favorites`,
      icon: Star,
      current: location.pathname === `${basePath}/favorites`,
    },
  ];

  // Generate dynamic event navigation from sections
  const eventNavigation =
    sections?.map((section) => ({
      name: section.name,
      href: `${basePath}/sections/${section.id}`,
      icon: getSectionIcon(section.icon),
      current: location.pathname === `${basePath}/sections/${section.id}`,
    })) || [];

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-900/80"
            onClick={() => setSidebarOpen(false)}
          />

          <div className="fixed inset-y-0 left-0 z-50 w-full bg-white shadow-xl">
            <div className="flex h-full flex-col">
              {/* Event Info and Close button */}
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center space-x-3 flex-1 min-w-0 px-3">
                  {eventLoading ? (
                    <>
                      <div className="h-10 w-10 bg-gray-200 rounded-lg animate-pulse"></div>
                      <div className="flex-1 min-w-0">
                        <div className="h-3 bg-gray-200 rounded w-3/4 mb-1 animate-pulse"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </>
                  ) : event ? (
                    <>
                      {getEventLogoUrl() ? (
                        <img
                          src={getEventLogoUrl()}
                          alt={getEventName()}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                          <X className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h2 className="text-sm font-semibold text-gray-900 truncate">
                          {getEventName()}
                        </h2>
                        <p className="text-xs text-gray-500 truncate">
                          {getEventDateRange()}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                        <X className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-sm font-semibold text-gray-900">
                          Event
                        </h2>
                        <p className="text-xs text-gray-500">Loading...</p>
                      </div>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Sticky Main Navigation */}
              <div className="flex-shrink-0">
                <nav className="px-4 py-2">
                  <ul className="space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center gap-x-3 rounded-md px-3 py-2 text-base font-medium"
                          )}
                          style={
                            item.current
                              ? {
                                  backgroundColor: themeColor || "#3B82F6",
                                }
                              : {}
                          }
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-500 group-hover:text-gray-700",
                              "h-5 w-5 flex-shrink-0"
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Separator */}
                  <div className="my-2 border-t border-gray-200"></div>

                  {/* Social Navigation */}
                  <ul className="space-y-1">
                    {socialNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center gap-x-3 rounded-md px-3 py-2 text-base font-medium"
                          )}
                          style={
                            item.current
                              ? {
                                  backgroundColor: themeColor || "#3B82F6",
                                }
                              : {}
                          }
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-500 group-hover:text-gray-700",
                              "h-5 w-5 flex-shrink-0"
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Separator */}
                  <div className="my-2 border-t border-gray-200"></div>
                </nav>
              </div>

              {/* Scrollable Event Navigation Section */}
              <div className="flex-1 overflow-y-auto">
                <nav className="px-4 py-2">
                  <ul className="space-y-1">
                    {eventNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center gap-x-3 rounded-md px-3 py-2 text-base font-medium"
                          )}
                          style={
                            item.current
                              ? {
                                  backgroundColor: themeColor || "#3B82F6",
                                }
                              : {}
                          }
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-500 group-hover:text-gray-700",
                              "h-5 w-5 flex-shrink-0"
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              {/* Banner Carousel - sticky bottom */}
              {banners && banners.length > 0 && (
                <div className="flex-shrink-0">
                  <BannerCarousel
                    banners={banners}
                    eventSlug={eventSlug}
                    setSidebarOpen={setSidebarOpen}
                  />
                </div>
              )}

              {/* Powered by section - sticky bottom */}
              <div className="flex-shrink-0 border-t border-gray-200 p-2">
                <div className="flex items-center justify-center">
                  <PoweredByWave size="xs" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex h-full flex-col border-r border-gray-200 bg-white">
          {/* Event Info */}
          <div className="flex shrink-0 items-center py-2 px-6">
            {eventLoading ? (
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                </div>
              </div>
            ) : event ? (
              <div className="flex items-center space-x-3">
                {getEventLogoUrl() ? (
                  <img
                    src={getEventLogoUrl()}
                    alt={getEventName()}
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                    <X className="h-6 w-6 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900 truncate">
                    {getEventName()}
                  </h2>
                  <p className="text-xs text-gray-500 truncate">
                    {getEventDateRange()}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                  <X className="h-6 w-6 text-gray-500" />
                </div>
                <div className="flex-1">
                  <h2 className="text-sm font-semibold text-gray-900">Event</h2>
                  <p className="text-xs text-gray-500">Loading...</p>
                </div>
              </div>
            )}
          </div>

          {/* Sticky Main Navigation */}
          <div className="flex-shrink-0">
            <nav className="px-6">
              <ul className="flex flex-col gap-y-2 py-2">
                <li>
                  <ul className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold"
                          )}
                          style={
                            item.current
                              ? {
                                  backgroundColor: themeColor || "#3B82F6",
                                }
                              : {}
                          }
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-500 group-hover:text-gray-700",
                              "h-6 w-6 shrink-0"
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Separator */}
                <li>
                  <div className="border-t border-gray-200"></div>
                </li>

                {/* Social Navigation */}
                <li>
                  <ul className="-mx-2 space-y-1">
                    {socialNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold"
                          )}
                          style={
                            item.current
                              ? {
                                  backgroundColor: themeColor || "#3B82F6",
                                }
                              : {}
                          }
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-500 group-hover:text-gray-700",
                              "h-6 w-6 shrink-0"
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Separator */}
                <li>
                  <div className="border-t border-gray-200"></div>
                </li>
              </ul>
            </nav>
          </div>

          {/* Scrollable Event Navigation Section */}
          <div className="flex-1 overflow-y-auto">
            <nav className="px-6">
              <ul className="-mx-2 space-y-1 py-2">
                {eventNavigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                        item.current
                          ? "text-white"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                        "group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold"
                      )}
                      style={
                        item.current
                          ? {
                              backgroundColor: themeColor || "#3B82F6",
                            }
                          : {}
                      }
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-white"
                            : "text-gray-500 group-hover:text-gray-700",
                          "h-6 w-6 shrink-0"
                        )}
                      />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* User profile - sticky bottom */}
          <div className="flex-shrink-0 border-t border-gray-200">
            <Link
              to={`${basePath}/profile`}
              className="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
            >
              {attendee?.avatar_url ? (
                <img
                  src={attendee.avatar_url}
                  alt={getAttendeeName()}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: themeColor || "#3B82F6" }}
                >
                  <span className="text-white font-bold text-sm">
                    {getAttendeeInitials()}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {getAttendeeName()}
                </p>
              </div>
              <Settings className="h-4 w-4 text-gray-500" />
            </Link>
          </div>
          {/* Banner Carousel - sticky bottom */}
          {banners && banners.length > 0 && (
            <div className="flex-shrink-0">
              <BannerCarousel
                banners={banners}
                eventSlug={eventSlug}
                setSidebarOpen={setSidebarOpen}
              />
            </div>
          )}

          {/* Powered by section - sticky bottom */}
          <div className="flex-shrink-0 flex items-center justify-center px-6 py-2 border-t border-gray-200">
            <PoweredByWave size="xs" />
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 flex items-center gap-x-4 bg-white px-4 py-2 shadow-sm sm:px-6 lg:hidden mobile-header">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900"
        >
          <span className="sr-only">Open sidebar</span>
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          {(() => {
            const currentItem = [
              ...navigation,
              ...socialNavigation,
              ...eventNavigation,
            ].find((item) => item.current);
            if (currentItem) {
              const IconComponent = currentItem.icon;
              return (
                <>
                  <IconComponent className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-900">
                    {currentItem.name}
                  </span>
                </>
              );
            }
            return (
              <span className="text-sm font-semibold text-gray-900">Wave</span>
            );
          })()}
        </div>

        {/* Mobile actions (filter and search icons) */}
        {actions.length > 0 && (
          <div className="flex items-center gap-1">
            {actions.map((action, index) => {
              const IconComponent = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors relative"
                  title={action.title}
                >
                  <IconComponent className="w-5 h-5" />
                  {action.badge && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {action.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div
          className="h-8 w-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: themeColor || "#3B82F6" }}
        >
          <Link to={`${basePath}/profile`}>
            {attendee?.avatar_url ? (
              <img
                src={attendee.avatar_url}
                alt={getAttendeeName()}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: themeColor || "#3B82F6" }}
              >
                <span className="text-white font-bold text-sm">
                  {getAttendeeInitials()}
                </span>
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile search input - shown when searchProps is provided */}
      {searchProps && (
        <div className="lg:hidden px-4 py-2 border-t border-gray-200 bg-white">
          <input
            type="text"
            placeholder="Search..."
            value={searchProps.value}
            onChange={searchProps.onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ "--tw-ring-color": themeColor || "#3B82F6" }}
          />
        </div>
      )}
    </>
  );
}
